import { Observable } from 'rxjs';
import { FieldTypeDefinitionModel } from '../custom-data/models/field-type-definition.model';
import {
  ObjectDefinitionConfigurationModel,
  ObjectDefinitionModel,
} from '../custom-data/models/object-definition.model';
import { ObjectKeyMappingModel } from '../custom-data/models/object-key-mapping.model';
import { UiModule } from '../generic-ui/models/ui-module.model';
import * as _ from 'underscore';
import { inject } from '@angular/core';
import { NotificationService } from '@seahorse/common';
import { objectKeyMappings } from './object-key-mappings';
import {
  AddressTypeModel,
  NotificationServerModel,
} from '../notifications/_index';
import { CustomDataAdditionalDataModel } from '../custom-data/_index';

export abstract class ProxyService {
  fieldTypeDefinitions: FieldTypeDefinitionModel[] = [];
  objectDefinitions: ObjectDefinitionModel[] = [];
  uiModules: UiModule[] = [];
  enabledFeatures: string[] = [];
  objectKeyMapping: Record<string, ObjectKeyMappingModel>;
  notificationAddressTypes: AddressTypeModel[] = [];
  notificationServers: NotificationServerModel[] = [];
  abstract getData(): Observable<boolean>;

  defaultKeyMappings = objectKeyMappings;
  protected _notificationService = inject(NotificationService);

  getBaseObjectDefinitionById(id: number): ObjectDefinitionModel {
    return this.objectDefinitions.find(
      (filter) =>
        filter.baseObjectDefinition && filter.baseObjectDefinition.id === id
    )?.baseObjectDefinition;
  }

  getBaseObjectDefinitionByMappingKey(
    mappingKey: string
  ): ObjectDefinitionModel {
    return _.find(this.objectDefinitions, (filter) => {
      return (
        filter.baseObjectDefinition &&
        filter.baseObjectDefinition.mappingKey === mappingKey
      );
    })?.baseObjectDefinition;
  }

  getBaseObjectDefinitionBySystemCode(
    systemCode: string
  ): ObjectDefinitionModel {
    return _.find(this.objectDefinitions, (filter) => {
      return (
        filter.baseObjectDefinition &&
        filter.baseObjectDefinition.systemCode === systemCode
      );
    })?.baseObjectDefinition;
  }

  getObjectDefinitionById(id: number): ObjectDefinitionModel {
    return this.objectDefinitions.find((obj) => obj.id === id);
  }

  getObjectDefinitionByMappingKey(mappingKey: string): ObjectDefinitionModel {
    return this.objectDefinitions.find(
      (def) => def.mappingKey.toLowerCase() === mappingKey?.toLowerCase()
    );
  }

  getObjectDefinitionBySystemCode(systemCode: string): ObjectDefinitionModel {
    return this.objectDefinitions.find(
      (def) => def.systemCode.toLowerCase() === systemCode?.toLowerCase()
    );
  }

  getObjectDefinitionByAdditionalDataModel(
    additionalDataModel: CustomDataAdditionalDataModel
  ): ObjectDefinitionModel | null {
    let objectDefinition: ObjectDefinitionModel | null = null;
    const linkedObjectMappingKey =
      additionalDataModel.mappingKey?.split('_')[1] ?? null;
    if (linkedObjectMappingKey) {
      if (additionalDataModel.overrideBase === false)
        objectDefinition = this.getBaseObjectDefinitionBySystemCode(
          linkedObjectMappingKey
        );

      if (!objectDefinition)
        objectDefinition = this.getObjectDefinitionBySystemCode(
          linkedObjectMappingKey
        );
    }
    return objectDefinition;
  }

  getUIModuleForObjectDefinition(definitionId: number) {
    return _.find(
      this.uiModules,
      (moduleFind) => moduleFind.customObjectDefinitionId === definitionId
    );
  }

  getNotificationAddressType(systemCode: string) {
    return !this.notificationAddressTypes
      ? null
      : this.notificationAddressTypes.find(
          (x) => x.systemCode.toLowerCase() === systemCode.toLowerCase()
        );
  }

  getNotificationAddressTypeById(id: number) {
    return !this.notificationAddressTypes
      ? null
      : this.notificationAddressTypes.find((x) => x.id === id);
  }

  protected setDefaultKeyMappings() {
    this.objectKeyMapping = {
      ...this.defaultKeyMappings,
    };

    this.objectKeyMapping = {
      ...this.objectKeyMapping,
      ...this.createObjectKeyMappings(this.objectDefinitions),
    };
  }

  createObjectKeyMappings(definitions: ObjectDefinitionModel[]) {
    const objectKeyMapping: Record<string, ObjectKeyMappingModel> = {};

    if (!definitions || definitions.length === 0) {
      return objectKeyMapping;
    }

    _.each(definitions, (def: ObjectDefinitionModel) => {
      const mappings = this.createObjectKeyMapping(def);

      _.each(mappings, (mapping, key) => {
        objectKeyMapping[key] = mapping;
      });
    });

    return objectKeyMapping;
  }

  createObjectKeyMapping(definition: ObjectDefinitionModel) {
    const mappings: Record<string, ObjectKeyMappingModel> = {};

    if (!definition?.templateCode) {
      return mappings;
    }

    try {
      const config: ObjectDefinitionConfigurationModel = JSON.parse(
        definition.templateCode
      );
      if (
        config &&
        config.link &&
        config.link.allowTolink === true &&
        config.link.mapping
      ) {
        _.each(config.link.mapping.keys, (key) => {
          mappings[
            `$${config.link.mapping.moduleKey}_${
              config.link.mapping.objectKey
            }_${key.toLowerCase()}`
          ] = config.link.mapping;
        });
      }
    } catch (error: any) {
      this._notificationService.displayErrorNotification(error);
    }

    return mappings;
  }
}
